import { useEffect, useRef, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Menu, ShieldCheck, X } from "lucide-react";
import { AdminSidebar } from "./AdminSidebar";
import { Logo } from "@/components/ui/Logo";
import { UserMenu } from "@/components/UserMenu";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SEOHead } from "@/components/SEO";
import { cn } from "@/lib/utils";
import "@/styles/account-redesign.css";

const pageNames: Record<string, string> = {
  users: "People",
  replays: "Replays",
  analytics: "Analytics",
  announcements: "Announcements",
  comments: "Comments",
  "contact-messages": "Inbox",
  feedbacks: "Feedback",
  activity: "Activity log",
  jobs: "Processing & jobs",
  tokens: "Access tokens",
};

export function AdminLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuTrigger = useRef<HTMLButtonElement>(null);
  const sidebarPanel = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const currentPage = pageNames[location.pathname.split("/")[2]] || "Overview";
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 768px)");
    const closeOnDesktop = () => {
      if (desktop.matches) setMobileMenuOpen(false);
    };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const focusable = () =>
      Array.from(
        sidebarPanel.current?.querySelectorAll<HTMLElement>(
          "a[href], button:not([disabled])",
        ) || [],
      );
    const frame = requestAnimationFrame(() => focusable()[0]?.focus());
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
        menuTrigger.current?.focus();
      }
      if (event.key === "Tab") {
        const elements = focusable();
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKey);
    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKey);
    };
  }, [mobileMenuOpen]);
  return (
    <div className="bc-admin">
      <SEOHead
        title={`${currentPage} · Admin`}
        description="BallCam administration. Manage the community, content, and platform operations."
        noIndex
      />
      <a
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-4 focus:z-[100] bc-button bc-button-primary"
        href="#admin-main"
      >
        Skip to content
      </a>
      <header className="bc-admin-topbar">
        <div className="flex items-center gap-4">
          <button
            ref={menuTrigger}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-400"
            aria-expanded={mobileMenuOpen}
            aria-controls="admin-sidebar"
            aria-label={mobileMenuOpen ? "Close navigation" : "Open navigation"}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <Link to="/" aria-label="BallCam home">
            <Logo size="md" />
          </Link>
          <span className="bc-admin-topbar-label">Administration</span>
        </div>
        <div className="flex items-center gap-6">
          <Link
            to="/"
            className="hidden sm:flex items-center gap-2 text-xs text-gray-400 hover:text-white"
          >
            Open site <ArrowUpRight size={14} />
          </Link>
          <UserMenu />
        </div>
      </header>
      {mobileMenuOpen && (
        <button
          tabIndex={-1}
          aria-label="Close navigation"
          className="md:hidden fixed inset-0 z-[65] bg-black/70 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      <div className="bc-admin-body">
        <div
          id="admin-sidebar"
          ref={sidebarPanel}
          role={mobileMenuOpen ? "dialog" : undefined}
          aria-modal={mobileMenuOpen || undefined}
          aria-label={mobileMenuOpen ? "Administration navigation" : undefined}
          className={cn("md:block", mobileMenuOpen ? "block" : "hidden")}
        >
          <AdminSidebar
            onClose={() => setMobileMenuOpen(false)}
            isMobile={mobileMenuOpen}
          />
        </div>
        <main id="admin-main" className="bc-admin-main">
          <div className="bc-admin-breadcrumb">
            <ShieldCheck size={13} />
            <Link to="/admin">Control room</Link>
            <ChevronRight size={12} />
            <span>{currentPage}</span>
          </div>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
export default AdminLayout;
